import {FC, useCallback, useContext, useEffect, useRef, useState} from "react";
import {useLocation, useMatch, useNavigate, useParams} from "react-router-dom";
import {Box, Button, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {ModuleCompleteButton} from "@buildwitt/shapeshift-common/v1/module";
import {PageTitle} from "@buildwitt/shapeshift-common/v1/pageBase";
import {Files} from "@buildwitt/shapeshift-common/v1/files";
import {Links} from "@buildwitt/shapeshift-common/v1/links";
import {INotificationSeverity, APIState, NotificationContext} from "@buildwitt/shapeshift-identity";
import {CenteredContainer} from "@/components/pageBase/centeredContainer";
import {CurriculumContext} from "@/contexts/curriculumContext";
import {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import {getModule} from "@buildwitt/shapeshift-common/v1/common";
import {ICourseCurriculumModule, IModuleEnrollment} from "@buildwitt/shapeshift-types";
import {getAllFontStyles} from "@/templates/defaultTheme";
import {TrainingfeedContext} from "@/contexts/trainingFeedContext";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v1/wysiwyg";
import {bitmovinPlayerConfig, palette, toolbarHeight} from "@/constants";
import {Player, PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import EngineBitmovinModule from "bitmovin-player/modules/bitmovinplayer-engine-bitmovin";
import MseRendererModule from "bitmovin-player/modules/bitmovinplayer-mserenderer";
import HlsModule from "bitmovin-player/modules/bitmovinplayer-hls";
import DashModule from "bitmovin-player/modules/bitmovinplayer-dash";
import AbrModule from "bitmovin-player/modules/bitmovinplayer-abr";
import XmlModule from "bitmovin-player/modules/bitmovinplayer-xml";
import ContainerTSModule from "bitmovin-player/modules/bitmovinplayer-container-ts";
import ContainerMp4Module from "bitmovin-player/modules/bitmovinplayer-container-mp4";
import SubtitlesModule from "bitmovin-player/modules/bitmovinplayer-subtitles";
import SubtitlesCEA608Module from "bitmovin-player/modules/bitmovinplayer-subtitles-cea608";
import PolyfillModule from "bitmovin-player/modules/bitmovinplayer-polyfill";
import StyleModule from "bitmovin-player/modules/bitmovinplayer-style";
import "bitmovin-player/bitmovinplayer-ui.css";
import {UIFactory} from "bitmovin-player-ui";
import {SocketContext} from "@/contexts/socketContext";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {CompletionCelebrationWrap} from "@/pages/CoursePage/CourseCompletionCelebration/CompletionCelebrationWrap";
import { IAnnouncement } from "@/types/announcement";
import StreakDrawer from "../LessonPage/StreakDrawer/StreakDrawer";
import { AnnouncementVideoPlayer } from "./AnnouncementVideoPlayer";
import { AnnouncementContext } from "@/contexts/announcementContext";
import { useSetAnnouncementCompletion } from "@/hooks/useSetAnnouncementCompletion";
import { Badge } from "@/components/badge/Badge";
import {useOrientation} from "@/hooks/useOrientation";
import {landscapeVideoContainerStyles} from "@/styles/landscapeVideoPlayerStyles";

const useStyles = makeStyles<{ scrollToContinueBannerEnabled: boolean; landscapeVideoFullscreen: boolean }>()((theme, {scrollToContinueBannerEnabled, landscapeVideoFullscreen}) => ({
	landscapeVideoContainer: landscapeVideoFullscreen ? landscapeVideoContainerStyles : {},
	miniQuizzer: {
		"[class*='submitBtn']": {
			[theme.breakpoints.down("sm")]: {
				width: "100%"
			}
		}

	},
	resumeVideoContainer: {
		position: "fixed",
		top: toolbarHeight,
		margin: "20px 30px",
		right: "0px",
		zIndex: 30
	},
	badgeContainer: {
		display: "flex",
		marginTop: "32px",
		marginBottom: "30px",
		gap: "10px",
		"p": {
			color: theme.palette.common.black
		},
		"& > div:nth-of-type(1)": {
			background: theme.palette.primary.main
		}
	},
	title: {
		color: theme.palette.common.white,
		"h2": {
			fontSize: "42px"
		},
		"h5": {
			fontSize: "20px",
			lineHeight: "1.5rem"
		},
		[theme.breakpoints.down("sm")]: {
			marginBottom: "30px"
		}
	},
	centeredContainer: {
		maxWidth: "900px",
		padding: "0 20px",
		paddingBottom: "100px"
	},
	announcementCompleteBtn: {
		width: "200px",
		[theme.breakpoints.down("sm")]: {
			width: "100%"
		},
		float: "right"
	},
	content: {
		...getAllFontStyles(theme),
		color: theme.palette.text.primary,
		marginBottom: "40px",
		"& p": {
			lineHeight: 1.5,
			fontSize: "17px"
		},
		"& a": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	contentImage: {
		maxWidth: "100%"
	},
	attachmentsSectionTitle: {
		color: "#FFFFFF",
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 600,
		fontSize: "20px",
		lineHeight: "150%",
		letterSpacing: "0.15px",
		margin: "20px 0"
	},
	scheduledDeliveryDate: {
		color: "#F9F9F9B2",
		paddingBottom: theme.spacing(1),
		fontWeight: 400,
		fontSize: "12px",
		textDecoration: "uppercase",
	},
	files: {
		marginBottom: "40px",
		"& > div": {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			background: "transparent",
			padding: "12px 0",
			marginBottom: "8px",
			gap: "8px",
			position: "relative",
			paddingLeft: "22px"
		},
		"& > div::before": {
			content: '""',
			position: "absolute",
			left: 0,
			top: "50%",
			transform: "translateY(-50%)",
			width: "18px",
			height: "18px",
			flexShrink: 0,
			backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${theme.palette.primary.main}" d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5C7 20.54 9.46 23 12.5 23s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>`
			)}")`,
			backgroundRepeat: "no-repeat",
			backgroundPosition: "center",
			backgroundSize: "contain"
		},
		"& > div > div:first-of-type": {
			display: "none"
		},
		"& > div > div:nth-of-type(2)": {
			margin: "0 !important",
			flexGrow: 1,
			minWidth: 0
		},
		"& > div > div:nth-of-type(2) h6": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			color: theme.palette.primary.main,
			margin: 0
		},
		"& > div > a": {
			position: "absolute",
			left: 0,
			top: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
			border: "none",
			padding: 0,
			background: "transparent"
		},
		"& > div > a > div": {
			visibility: "hidden"
		}
	},
	links: {
		marginBottom: "40px",
		"& a": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			textDecorationSkipInk: "auto",
			color: theme.palette.primary.main
		}
	},
	scrollToContinueBanner: {
		opacity: scrollToContinueBannerEnabled ? 1 : 0,
		transition: "opacity 0.2s ease-in-out",
		position: "absolute",
		color: theme.palette.primary.main,
		width: "100%",
		textAlign: "center",
		paddingTop: "35%",
		height: "100%",
		[theme.breakpoints.down("lg")]: {
			paddingTop: theme.spacing(4.5),
			paddingBottom: theme.spacing(0.5),
			height: "auto"
		},
		pointerEvents: "none",
		justifyContent: "center",
		margin: "auto",
		zIndex: 10,
		"& svg": {
			fontSize: 50,
			padding: 0,
			margin: 0,
			[theme.breakpoints.down("sm")]: {
				fontSize: 30
			}
		},
		"& > *": {
			pointerEvents: "auto",
			cursor: scrollToContinueBannerEnabled ? "pointer" : "default"
		},
		background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2))"

	}
}));

interface IProps {
	announcement: IAnnouncement,
	isForHomePage?: boolean
}

export const AnnouncementPageContent: FC<IProps> = ({announcement, isForHomePage}) => {
	const {trainingfeedState} = useContext(TrainingfeedContext);
	const [videoPlaying, setVideoPlaying] = useState(false);
	const [miniQuizzerDisabled, setMiniQuizzerDisabled] = useState(true);
	const [scrollToContinueBannerEnabled, setScrollToContinueBannerEnabled] = useState(false);
	const isLandscape = useOrientation();
	const landscapeVideoFullscreen = isLandscape && !!announcement?.videoUrl && !!isForHomePage;
	const {cx, classes} = useStyles({scrollToContinueBannerEnabled, landscapeVideoFullscreen});
	const pageIsFeed = useMatch("/feed/:courseSlug/*");
	const {moduleSlug: announcementSlug} = useParams();
	const {curriculumState: curriculum, curriculumDispatch} = useContext(CurriculumContext);
	const [{contentContainerRef, memberedCohort}] = useContext(PageBaseContext);
	const {trainingfeedDispatch} = useContext(TrainingfeedContext);
	const {notify} = useContext(NotificationContext);
	const navigate = useNavigate();
	const [firstAvailableModule, setFirstAvailableModule] = useState<ICourseCurriculumModule | false>();
	const videoRef = useRef<VideoPlayerRef | null>(null);
	const buttonScrollRef = useRef<HTMLSpanElement>(null);
	const [player, setPlayer] = useState<PlayerAPI | null>(null);
	const {announcementState, announcementDispatch} = useContext(AnnouncementContext);
	(global as any).playerx = player;
	const [videoFinished, setVideoFinished] = useState(false);
	const socket = useContext(SocketContext)
	const location = useLocation();
	const [quizComplete, setQuizComplete] = useState(false);
	const videoContainerRef = useRef<HTMLDivElement | null>(null);
	const [openStreakDrawer, setOpenStreakDrawer] = useState(false);
	const [streakDays, setStreakDays] = useState<number>(0);
	const [openCelebration, setOpenCelebration] = useState(false);
	const [pendingStreak, setPendingStreak] = useState<{days: number} | null>(null);
	const {setComplete, apiResult: completeResponse} = useSetAnnouncementCompletion();

	useEffect(() => {
		setVideoFinished(!announcement.videoUrl);
		setQuizComplete(!(announcement as any).quiz);
		setScrollToContinueBannerEnabled(false)
	}, [announcement])

	useEffect(() => {
		handleCompletion(completeResponse)
	}, [completeResponse])


	useEffect(() => {
		const updateScrollBanner = (): void => {
			if (!contentContainerRef?.current) return;

			const scrollableDistance = contentContainerRef.current.scrollHeight - contentContainerRef.current.clientHeight;
			const hasScrollDistance = (scrollableDistance > 50);

			setScrollToContinueBannerEnabled(!videoPlaying && !miniQuizzerDisabled && hasScrollDistance);
		};

		updateScrollBanner();
		window.addEventListener("resize", updateScrollBanner);

		return () => {
			window.removeEventListener("resize", updateScrollBanner);
		};
	}, [videoPlaying, miniQuizzerDisabled]);

	useEffect(() => {
		if (!scrollToContinueBannerEnabled) return;
		const doc = document as Document & {
			webkitFullscreenElement?: Element;
			webkitExitFullscreen?: () => Promise<void>;
			webkitCancelFullScreen?: () => void;
			mozCancelFullScreen?: () => void;
			msExitFullscreen?: () => void;
		};
		const isFullscreen = Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement);
		if (!isFullscreen) return;
		const exit =
			doc.exitFullscreen ??
			doc.webkitExitFullscreen ??
			doc.webkitCancelFullScreen ??
			doc.mozCancelFullScreen ??
			doc.msExitFullscreen;
		if (exit) {
			const result = exit.call(doc);
			if (result?.catch) result.catch(() => {});
		}
	}, [scrollToContinueBannerEnabled]);

	useEffect(() => {
		videoRef.current = null;
		if (contentContainerRef?.current) {
			contentContainerRef.current.scrollTo({top: 0, behavior: "smooth"});
		}
	}, [announcementSlug])

	const handleVideoReady = useCallback((announcement: IAnnouncement) => {
		if (!process.env.REACT_APP_BITMOVIN_PLAYER_KEY)
			throw new Error("REACT_APP_BITMOVIN_PLAYER_KEY required")

		if (!videoRef.current?.bitmovin?.container)
			return;

		const playerSource = {
			hls: announcement.videoUrl,
			poster: announcement.videoThumbnailUrl
		};

		Player.addModule(EngineBitmovinModule);
		Player.addModule(MseRendererModule);
		Player.addModule(HlsModule);
		Player.addModule(XmlModule);
		Player.addModule(DashModule);
		Player.addModule(AbrModule);
		Player.addModule(ContainerTSModule);
		Player.addModule(ContainerMp4Module);
		Player.addModule(SubtitlesModule);
		Player.addModule(SubtitlesCEA608Module);
		Player.addModule(PolyfillModule);
		Player.addModule(StyleModule);

		// Delay player setup for Safari
		setTimeout(() => {
			if (!videoRef.current?.bitmovin?.container)
				return;

			const playerInstance = new Player(videoRef.current.bitmovin?.container, bitmovinPlayerConfig);
			UIFactory.buildDefaultUI(playerInstance);
			playerInstance.load(playerSource).then(() => {
				setPlayer(playerInstance);
				console.log("Successfully loaded source");
			}, (e) => {
				console.log("Error while loading source", e);
			});
		}, 100); // Delay added
	}, [])

	useEffect(() => {
		if (announcementSlug && curriculum){
			if(!pageIsFeed)
				setFirstAvailableModule(getModule(curriculum) ?? false)
			else if(trainingfeedState){
				for(const day of ["overdue", ...Object.keys(trainingfeedState)]){
					if(trainingfeedState[day] === "loading"){
						//day still loading, wait and re-run on dep change
						return;
					}
					for(const module of trainingfeedState[day]){
						if(typeof module !== "string" && !module.complete){
							setFirstAvailableModule(module);
							return;
						}
					}
				}
			}
		}
	}, [announcementSlug, curriculum, trainingfeedState])

	const navigateToNext = () => {
		navigate("/feed/")
	}

	const handleCompletion = useCallback((e: APIState<IModuleEnrollment>): void => {
		if(e.isError) {
			notify("Please try again", "Something went wrong", INotificationSeverity.error, 5000);
		}
		else if(!e.isLoading && !e.isError && e.statusCode === 200 && e.data?.id){
			announcementDispatch({
				type: "setAnnouncementComplete", id: e.data?.id})

			if(e?.data?.complete){
				notify(null, "Announcement completed", INotificationSeverity.success, 5000);
				navigate("/feed/")
			}
		}
	}, [announcementDispatch, announcementSlug])

	const handleCompleteCheck = (announcement: IAnnouncement, sendNotification: boolean): boolean => {
		if (announcement.videoUrl && !videoFinished) {
			sendNotification && notify("Please finish the video before progressing", "Hold up!", INotificationSeverity.warning, 5000)
			return false;
		}
		if ((announcement as any).quiz && !quizComplete) {
			sendNotification && notify("Please attempt the quiz before progressing", "Hold up!", INotificationSeverity.warning, 5000)
			return false;
		}

		return true;
	}

	const prevProgress = useRef<number>()
	//BITMOVIN PLAYER PROGRESS
	useEffect(() => {
		if (!player) {
			return;
		}

		//run in loop to save video progress
		const interval = setInterval(() => {
			const started = (player?.getCurrentTime() ?? 0) > 0;
			const finished = (player.getDuration() - player.getCurrentTime()) < 5;
			if(finished) setVideoFinished(true);

			if (started && prevProgress.current !== player.getCurrentTime()) {
				prevProgress.current = player.getCurrentTime();
				socket?.emit("announcementVideoProgress", {progress: player.getCurrentTime(), announcementSlug})
			}

			setVideoPlaying(player.isPlaying())
		}, 500);

		return () => clearInterval(interval);
	}, [player]);

	const handleReactVideoProgress = (e: {playedSeconds: number}): void => {
		if(videoPlaying){
			socket?.emit("announcementVideoProgress", {progress: e.playedSeconds, announcementSlug})
		}
	}

	const handleClickContinue = () => {
		setOpenStreakDrawer(false);
		setStreakDays(0);
		if (curriculum && curriculum.percentCompleted === 100){
			navigate("/training-feed")
		}else {
			navigateToNext();
		}
	}

	useEffect(() => setMiniQuizzerDisabled(!videoFinished), [videoFinished])
	useEffect(() => (announcement.complete) ? setMiniQuizzerDisabled(false) : undefined, [announcement?.complete])

	const handleCelebrationClose = () => {
		setOpenCelebration(false);
		if (pendingStreak) {
			setOpenStreakDrawer(true);
			setStreakDays(pendingStreak.days);
			setPendingStreak(null);
		}else {
			if (curriculum && curriculum.percentCompleted === 100){
				navigate("/training-feed")
			}else {
				navigateToNext();
			}
		}
	};

	function formatDate(date: Date): string {
		const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
		const day = date.getDate();
		const year = date.getFullYear();

		const suffix = (n) => {
		  if (n % 10 === 1 && n % 100 !== 11) return "ST";
		  if (n % 10 === 2 && n % 100 !== 12) return "ND";
		  if (n % 10 === 3 && n % 100 !== 13) return "RD";
		  return "TH";
		};

		return `${month} ${day}${suffix(day)}, ${year}`;
	}

	return (<>
		<Box ref={videoContainerRef} className={cx(landscapeVideoFullscreen && classes.landscapeVideoContainer)}>
			<AnnouncementVideoPlayer
				componentOverrides={!isForHomePage ? {
					header: <Box
						onClick={() => (contentContainerRef.current && scrollToContinueBannerEnabled) && contentContainerRef.current.scrollBy({top: videoContainerRef.current?.offsetHeight, behavior: "smooth"})}
						className={classes.scrollToContinueBanner}
					>
						<KeyboardDoubleArrowDownIcon /><Typography variant="h5">Please scroll to continue</Typography>
					</Box>
				} : null}
				onReady={() => announcement ? handleVideoReady(announcement) : null}
				ref={videoRef}
				announcement={announcement}
				currentTime={location?.state?.timestamp ?? announcement?.videoTime}
				bitmovinPlayer={player}
				onVideoFinished={() => setVideoFinished(true)}
				onPlay={() => setVideoPlaying(true)}
				onPause={() => setVideoPlaying(false)}
				onEnded={() => {setVideoPlaying(false); setVideoFinished(true)}}
				onProgress={handleReactVideoProgress}

			/>
		</Box>
		{!isForHomePage &&
			<CenteredContainer className={classes.centeredContainer}>
				<Box className={classes.badgeContainer}>
					<Badge>
						COMPANY ANNOUNCEMENT
					</Badge>
				</Box>
				<Typography className={classes.scheduledDeliveryDate} variant="h6" color="textSecondary" gutterBottom>
					{announcement?.scheduledDeliveryDate && formatDate(new Date(announcement?.scheduledDeliveryDate ?? ""))}
				</Typography>
				<PageTitle className={classes.title} title={announcement?.title} subtitle={announcement?.subtitle} />
				<WYSIWYG className={classes.content}>
					{announcement?.content ?? ""}
				</WYSIWYG>

				{announcement?.files && <>
					<Typography className={classes.attachmentsSectionTitle} variant="h5">Files</Typography>
					<Files className={classes.files} files={announcement.files} />
				</>}

				{announcement?.links && announcement.links.length > 0 && <>
					<Typography className={classes.attachmentsSectionTitle} variant="h5">Links</Typography>
					<Links className={classes.links} links={announcement.links} />
				</>}
				<span ref={buttonScrollRef} />
				{announcement?.slug && !announcement?.complete &&
					<ModuleCompleteButton
						style={{opacity: videoFinished ? 1 : 0.5}}
						validate={() => handleCompleteCheck(announcement, true)}
						courseSlug={""}
						moduleSlug={announcement.slug}
						actionOverride={() => setComplete(announcement.slug)}
						type="announcement"
						className={classes.announcementCompleteBtn}
						onApiUpdate={handleCompletion}
					/>
				}
				{announcement?.slug && announcement?.complete && firstAvailableModule &&
					<Button
						onClick={() => navigate("/training-feed")}
						className={classes.announcementCompleteBtn}
					>
						Next {firstAvailableModule ? firstAvailableModule?.type : ""}
					</Button>
				}
				<StreakDrawer
					open={openStreakDrawer && streakDays > 0}
					streakDays={streakDays}
					onContinue={handleClickContinue}
				/>
			</CenteredContainer>
		}
		<CompletionCelebrationWrap
			open={openCelebration}
			onClose={handleCelebrationClose}
		/>
	</>
	);
}